import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatCalendarDate } from '../../utils/dateTimeUtils';
import { attendanceCounts, olderArchiveToYmd, RECENT_MEETING_DAYS } from './meetingListUtils';

function stop(e) {
  e.stopPropagation();
}

function AttendanceSummary({ participants, myAttendance }) {
  if (myAttendance) {
    return (
      <Chip
        size="small"
        label={myAttendance}
        color={myAttendance === 'present' ? 'success' : myAttendance === 'absent' ? 'error' : 'default'}
        variant={myAttendance === 'pending' ? 'outlined' : 'filled'}
      />
    );
  }
  const c = attendanceCounts(participants);
  if (!c.total) return <Chip size="small" label="No teachers" variant="outlined" />;
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${c.present}/${c.total} present${c.pending ? ` · ${c.pending} pending` : ''}`}
    />
  );
}

export default function StaffMeetingList({
  meetings,
  pagination,
  loading,
  range,
  onRangeChange,
  search,
  onSearchChange,
  archiveFrom,
  archiveTo,
  onArchiveFromChange,
  onArchiveToChange,
  onPageChange,
  variant = 'manage',
  onEdit,
  onDelete,
  onAttendance,
}) {
  const searching = Boolean(String(search || '').trim());
  const archiveCutoff = archiveTo || olderArchiveToYmd();
  const caption = searching
    ? range === 'older'
      ? 'Searching older meetings'
      : 'Searching all meetings, including older ones'
    : range === 'older'
      ? `Meetings on or before ${formatCalendarDate(archiveCutoff)}`
      : `Upcoming and the last ${RECENT_MEETING_DAYS} days`;

  const emptyMessage = searching
    ? 'No meetings match your search.'
    : range === 'older'
      ? 'No older meetings in this range.'
      : `No meetings in the last ${RECENT_MEETING_DAYS} days. Search or open Older to find past meetings.`;

  return (
    <Box>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size="small"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search title, notes, or teacher"
          fullWidth
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip
            label="Recent"
            color={range === 'recent' ? 'primary' : 'default'}
            variant={range === 'recent' ? 'filled' : 'outlined'}
            onClick={() => onRangeChange('recent')}
          />
          <Chip
            label="Older"
            color={range === 'older' ? 'primary' : 'default'}
            variant={range === 'older' ? 'filled' : 'outlined'}
            onClick={() => onRangeChange('older')}
          />
          {range === 'older' ? (
            <>
              <TextField
                size="small"
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={archiveFrom}
                onChange={(e) => onArchiveFromChange(e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={archiveTo || archiveCutoff}
                onChange={(e) => onArchiveToChange(e.target.value)}
                sx={{ minWidth: 160 }}
              />
            </>
          ) : null}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {caption}
          {pagination?.total ? ` · ${pagination.total} meeting${pagination.total === 1 ? '' : 's'}` : ''}
        </Typography>
      </Stack>

      {loading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={1}>
          {(meetings || []).map((m) => (
            <Accordion key={m.id} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { my: 0.75, overflow: 'hidden' } }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ width: '100%', pr: 1 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {m.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCalendarDate(m.meetingDate)} · {m.startTime}–{m.endTime}
                      {m.durationHours ? ` · ${m.durationHours}h` : ''}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap onClick={stop} onFocus={stop}>
                    <AttendanceSummary participants={m.participants} myAttendance={variant === 'mine' ? m.myAttendance : null} />
                    {m.joinUrl ? (
                      <Button size="small" href={m.joinUrl} target="_blank" rel="noreferrer">
                        Join
                      </Button>
                    ) : null}
                    {variant === 'manage' && onEdit ? (
                      <Button size="small" onClick={() => onEdit(m)}>
                        Edit
                      </Button>
                    ) : null}
                    {variant === 'manage' && onDelete ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          if (window.confirm('Delete this meeting?')) onDelete(m.id);
                        }}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {m.agenda ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Agenda:</strong> {m.agenda}
                  </Typography>
                ) : null}
                {m.meetingNotes || m.notes ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Meeting notes:</strong> {m.meetingNotes || m.notes}
                  </Typography>
                ) : null}
                {variant === 'manage' ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Teacher</TableCell>
                          <TableCell>Attendance</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(m.participants || []).map((p) => (
                          <TableRow key={p.teacherId}>
                            <TableCell>{p.name}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                {['pending', 'present', 'absent'].map((status) => (
                                  <Chip
                                    key={status}
                                    size="small"
                                    label={status}
                                    color={
                                      p.attendance === status
                                        ? status === 'present'
                                          ? 'success'
                                          : status === 'absent'
                                            ? 'error'
                                            : 'default'
                                        : 'default'
                                    }
                                    variant={p.attendance === status ? 'filled' : 'outlined'}
                                    onClick={() =>
                                      onAttendance?.({
                                        meetingId: m.id,
                                        teacherId: p.teacherId,
                                        attendance: status,
                                      })
                                    }
                                  />
                                ))}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Your attendance: {m.myAttendance || 'pending'}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
          {!meetings?.length ? (
            <Typography color="text.secondary">{emptyMessage}</Typography>
          ) : null}
          {pagination?.pages > 1 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
              <Pagination
                count={pagination.pages}
                page={pagination.page || 1}
                onChange={(_e, next) => onPageChange(next)}
                color="primary"
              />
            </Box>
          ) : null}
        </Stack>
      )}
    </Box>
  );
}
